import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, effect, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { SharedService } from '../shared/services/shared.service';
import { SKIP_ERROR_TOAST } from '../interceptor/error.interceptor';

export interface PersonalizedNearbyCourseAddress {
  city?: string;
  state?: string;
  country?: string;
  street?: string;
  unit?: string;
  zipcode?: string;
  latitude?: string;
  longitude?: string;
}

export interface PersonalizedNearbyCourse {
  id: string;
  iGolfId?: string;
  name: string;
  courseName: string;
  address?: PersonalizedNearbyCourseAddress;
  conciergePhone?: string;
  description?: string;
  distance?: number;
  distanceUnit?: string;
  holes?: number;
  logoUrl?: string;
  videoUrl?: string;
  bannerImageId?: string;
  caddy?: string;
  thumbnailUrl?: string;
}

interface PersonalizedNearbyCourseListResponse {
  success: boolean;
  data: PersonalizedNearbyCourse[];
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class PersonalizationService {
  private readonly _destroyRef = inject(DestroyRef);

  private static readonly _selectedCourseStorageKey = 'selectedNearbyCourseId';
  private static readonly _cachedCoursesKey = 'cachedNearbyCourses';

  private readonly _course = signal<PersonalizedNearbyCourse | null>(null);
  readonly course = this._course.asReadonly();

  private readonly _nearbyCourses = signal<PersonalizedNearbyCourse[]>([]);
  readonly nearbyCourses = this._nearbyCourses.asReadonly();

  private readonly _selectedCourseId = signal<string | null>(null);
  readonly selectedCourseId = this._selectedCourseId.asReadonly();

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  private _lastKey: string | null = null;
  private _inFlightKey: string | null = null;
  private _inFlightRequest$: Observable<void> | null = null;

  private readonly _http = inject(HttpClient);
  private readonly _sharedService = inject(SharedService);
  constructor() {
    // Restore cached course data for instant first paint
    this._restoreCachedCourses();

    effect(() => {
      const gps = this._sharedService.gpsSignal();
      const loggedIn = this._sharedService.loggedIn();

      // Only load personalization once auth/status has confirmed the session
      // so the cookie is established before this authenticated API is called.
      if (loggedIn && gps.latitude != null && gps.longitude != null) {
        this.loadNearbyPersonalization(gps.latitude, gps.longitude)
          .pipe(takeUntilDestroyed(this._destroyRef))
          .subscribe();
      }
    });
  }

  private _restoreCachedCourses(): void {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return;

      // Only restore cache if GPS coordinates exist (user allowed location)
      const hasGps = window.localStorage.getItem('latitude') && window.localStorage.getItem('longitude');
      if (!hasGps) {
        window.localStorage.removeItem(PersonalizationService._cachedCoursesKey);
        return;
      }

      const cached = window.localStorage.getItem(PersonalizationService._cachedCoursesKey);
      if (!cached) return;

      const courses: PersonalizedNearbyCourse[] = JSON.parse(cached);
      if (!courses?.length) return;

      this._nearbyCourses.set(courses);

      let selectedId: string | null = null;
      try {
        selectedId = window.sessionStorage?.getItem(PersonalizationService._selectedCourseStorageKey) ?? null;
      } catch { /* ignore */ }

      const selected = selectedId ? courses.find((c) => c.id === selectedId) ?? null : null;
      const course = selected ?? courses[0];
      this._course.set(course);
      this._selectedCourseId.set(course.id);
    } catch { /* ignore */ }
  }

  private _cacheCourses(courses: PersonalizedNearbyCourse[]): void {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(PersonalizationService._cachedCoursesKey, JSON.stringify(courses));
      }
    } catch { /* ignore */ }
  }

  selectCourse(courseId: string): void {
    const selected = this._nearbyCourses().find((c) => c.id === courseId) ?? null;
    if (!selected) {
      return;
    }

    this._selectedCourseId.set(courseId);
    this._course.set(selected);

    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(PersonalizationService._selectedCourseStorageKey, courseId);
      }
    } catch {
      return;
    }
  }

  loadNearbyPersonalization(
    latitude: number | null | undefined,
    longitude: number | null | undefined
  ): Observable<void> {
    if (latitude == null || longitude == null) {
      return of(void 0);
    }

    const key = `${latitude},${longitude}`;
    if (this._lastKey === key) {
      return this._inFlightKey === key && this._inFlightRequest$ ? this._inFlightRequest$ : of(void 0);
    }
    this._lastKey = key;

    this._loading.set(true);

    const options = {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
      params: { latitude: latitude!, longitude: longitude! }
    };

    const request$ = this._http
      .get<PersonalizedNearbyCourseListResponse>(
        `${environment.courseApiUrl}course/personalized-nearby-course-list`,
        options
      )
      .pipe(
        tap((res) => {
          const nearbyCourses = res?.success ? (res.data ?? []) : [];
          this._nearbyCourses.set(nearbyCourses);
          this._cacheCourses(nearbyCourses);

          let selectedId = this._selectedCourseId();
          if (!selectedId) {
            try {
              if (typeof window !== 'undefined' && window.sessionStorage) {
                selectedId = window.sessionStorage.getItem(PersonalizationService._selectedCourseStorageKey);
              }
            } catch {
              selectedId = null;
            }
          }

          const selectedCourse = selectedId
            ? nearbyCourses.find((c) => c.id === selectedId) ?? null
            : null;
          const nextCourse = selectedCourse ?? (nearbyCourses[0] ?? null);
          this._course.set(nextCourse);
          this._selectedCourseId.set(nextCourse?.id ?? null);
        }),
        catchError(() => {
          this._course.set(null);
          this._nearbyCourses.set([]);
          this._selectedCourseId.set(null);
          return of(null);
        }),
        map(() => void 0),
        finalize(() => {
          this._loading.set(false);
          if (this._inFlightKey === key) {
            this._inFlightKey = null;
            this._inFlightRequest$ = null;
          }
        }),
        shareReplay(1)
      );

    this._inFlightKey = key;
    this._inFlightRequest$ = request$;
    return request$;
  }
}
